import api from "./api.service";

// ─── Interfaces ──────────────────────────────────────────────────────────────

export interface Country {
  id: number;
  iso2Country: string;
  name: string;
  phoneCode: string;
  air?: boolean;
  maritime?: boolean;
}

export interface CountryFull extends Country {
  send: boolean;
  receive: boolean;
}

// Raw API shapes (API returns isO2Country with capital O)
interface CountryRaw {
  id: number;
  isO2Country: string;
  name: string;
  phoneCode: string;
  air?: boolean;
  maritime?: boolean;
}

interface CountryFullRaw extends CountryRaw {
  send: boolean;
  receive: boolean;
}

interface CountryListsResponse {
  statusCode: number;
  senders: CountryRaw[];
  receivers: CountryRaw[];
}

interface CountryAllResponse {
  statusCode: number;
  countries: CountryFullRaw[];
}

// ─── Mappers ──────────────────────────────────────────────────────────────────

const mapCountry = (c: any): Country => ({
  id: c.id,
  iso2Country: c.isO2Country || c.iso2Country || c.isO2country || c.ISO2Country,
  name: c.name,
  phoneCode: c.phoneCode,
  air: c.air,
  maritime: c.maritime,
});

const mapCountryFull = (c: any): CountryFull => ({
  ...mapCountry(c),
  send: c.send,
  receive: c.receive,
});

// ─── Service ──────────────────────────────────────────────────────────────────

class CountryService {
  /**
   * GET /country-lists
   * Retorna dos listas: países que envían (senders) y países que reciben (receivers).
   */
  async getCountryLists(): Promise<{ senders: Country[]; receivers: Country[] }> {
    try {
      const response = await api.get<CountryListsResponse>("/country-lists");
      return {
        senders: (response.data.senders ?? []).map(mapCountry).filter(c => c.iso2Country?.toUpperCase() !== "CU"),
        receivers: (response.data.receivers ?? []).map(mapCountry).filter(c => c.iso2Country?.toUpperCase() !== "CU"),
      };
    } catch (error) {
      console.error("Error fetching country lists:", error);
      throw error;
    }
  }

  /**
   * GET /country-all
   * Retorna todos los países activos con sus flags de envío y recepción.
   */
  async getAllCountries(): Promise<CountryFull[]> {
    try {
      const response = await api.get<CountryAllResponse>("/country-all");
      return (response.data.countries ?? []).map(mapCountryFull).filter(c => c.iso2Country?.toUpperCase() !== "CU");
    } catch (error) {
      console.error("Error fetching all countries:", error);
      throw error;
    }
  }

  /**
   * GET /country
   * Retorna un país por su código ISO2.
   */
  async getCountry(iso2country: string): Promise<CountryFull | null> {
    try {
      const response = await api.get<any>("/country", {
        params: { iso2country }
      });
      if (response.data && response.data.countries) {
        return mapCountryFull(response.data.countries);
      }
      return null;
    } catch (error) {
      console.error(`Error fetching country ${iso2country}:`, error);
      throw error;
    }
  }
}

export const countryService = new CountryService();
